import type { ComponentConfig, Config } from "@puckeditor/core";
import { slugifyAnchorId } from "@/utils/anchorId";

export type AnchorProps = {
  anchor?: string;
};

export function anchorField() {
  return {
    type: "text" as const,
    label: "Section ID (anchor)",
  };
}

// Puck's ComponentConfig generics are strict; we intentionally widen here so the
// HOC can wrap any block config without per-block type gymnastics.
export function withAnchor(
  config: ComponentConfig<AnchorProps>,
): ComponentConfig<AnchorProps> {
  const originalRender = config.render;

  if (!originalRender) {
    return {
      ...config,
      fields: {
        anchor: anchorField(),
        ...config.fields,
      },
    };
  }

  return {
    ...config,
    fields: {
      anchor: anchorField(),
      ...config.fields,
    },
    render: (props) => {
      const content = originalRender(
        props as Parameters<NonNullable<typeof originalRender>>[0],
      );
      const anchorId = slugifyAnchorId(props.anchor ?? "");

      if (!anchorId) {
        return content;
      }

      return (
        <div id={anchorId} className="scroll-mt-24">
          {content}
        </div>
      );
    },
  };
}

export function applyAnchorsToComponents<
  TComponents extends Config["components"],
>(components: TComponents): TComponents {
  return Object.fromEntries(
    Object.entries(components).map(([key, config]) => [
      key,
      withAnchor(config as ComponentConfig<AnchorProps>),
    ]),
  ) as TComponents;
}
